import { useCallback, useEffect, useState } from "react";

import Card from "../../../components/ui/Card";
import Button from "../../../components/ui/Button";
import { fetchPaymentAlerts, resolvePaymentAlert } from "../api";
import { PaymentAlert } from "../types";

type PaymentAlertsProps = {
  onOpen: (orderId: string) => void;
};

function formatPrice(amount: number, currency: string) {
  return `$ ${Number(amount).toLocaleString("es-AR")} ${currency}`;
}

function formatFecha(iso: string) {
  const fecha = new Date(iso);

  return Number.isNaN(fecha.getTime()) ? iso : fecha.toLocaleString("es-AR");
}

// Cobros a revisar: lo que el webhook de Mercado Pago no pudo aplicar solo
// (un doble cobro, un pago por otro monto, un reembolso o un contracargo, una
// disputa). Desde acá no se devuelve nada: la devolución, si hace falta, se
// hace aparte y después la alerta se marca resuelta (queda quién y cuándo).
export function PaymentAlerts({ onOpen }: PaymentAlertsProps) {
  const [alertas, setAlertas] = useState<PaymentAlert[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    try {
      setAlertas(await fetchPaymentAlerts());
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudieron listar los cobros a revisar.");
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const resolver = async (alerta: PaymentAlert) => {
    const confirmado = window.confirm(
      `¿Ya está resuelto el cobro de la orden #${alerta.orderId}? (${alerta.descripcion})`
    );

    if (!confirmado) return;

    setBusy(alerta.paymentId);
    setError("");

    try {
      await resolvePaymentAlert(alerta.orderId, alerta.paymentId);
      setAlertas((actuales) => actuales.filter((a) => a.paymentId !== alerta.paymentId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo marcar resuelta la alerta.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <h2 style={{ margin: 0, color: "var(--text-primary)", fontSize: 20 }}>Cobros a revisar</h2>
        <Button variant="secondary" onClick={cargar} disabled={busy !== null}>
          Actualizar
        </Button>
      </div>

      {error && (
        <div style={{ marginTop: 12, color: "var(--danger)", fontSize: 13, fontWeight: 600 }}>{error}</div>
      )}

      {!error && alertas.length === 0 && (
        <p style={{ margin: "12px 0 0", color: "var(--text-secondary)", fontSize: 13 }}>
          No hay cobros a revisar.
        </p>
      )}

      {alertas.length > 0 && (
        <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          {alertas.map((alerta) => (
            <div
              key={alerta.paymentId}
              style={{
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: 12,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                <strong style={{ color: "var(--text-primary)" }}>
                  Orden #{alerta.orderId} · {alerta.descripcion}
                </strong>
                <div style={{ marginTop: 4 }}>
                  Pago {alerta.mpPaymentId ?? alerta.paymentId} · {alerta.status}
                  {alerta.statusDetail ? ` (${alerta.statusDetail})` : ""} ·{" "}
                  {formatPrice(alerta.amount, alerta.currency)} · orden {alerta.orderStatus} ·{" "}
                  {formatFecha(alerta.updatedAt)}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <Button variant="secondary" onClick={() => onOpen(alerta.orderId)}>
                  Ver orden
                </Button>
                <Button
                  variant="primary"
                  onClick={() => resolver(alerta)}
                  disabled={busy === alerta.paymentId}
                >
                  Marcar resuelta
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
